/**
 * design.md's Usage Event Pipeline, step 1: `GET /api/telemetry/token`
 * (emmi-backend PR #1280, ES256, 12h expiry, D-6). That endpoint may not be
 * deployed yet, and `apiUrl`/`getAppAuthToken` may legitimately be
 * unavailable — this module fails closed (returns null) in every one of
 * those cases instead of throwing, so callers never need a try/catch.
 */

export interface TelemetryTokenConfig {
  /** Backend API base URL (e.g. `https://.../api`). Empty disables the fetch entirely. */
  apiUrl: string;
  /** Reads the app's OWN existing session token — not the telemetry token itself. */
  getAppAuthToken: () => string | null;
}

interface CachedToken {
  token: string;
  expiresAt: number;
  /**
   * The app auth token this telemetry token was minted for (usage-event-
   * pipeline correction batch 4B item 1). A logout, token refresh, or a
   * different user signing in on a shared device all change the app's own
   * token — when they do, a token cached for the PREVIOUS identity must
   * never be handed to a new caller.
   */
  identityKey: string;
}

// Refresh 5 min before the real 12h server-side expiry (D-6) so a
// borderline-expired token is never handed to a caller.
const TOKEN_SAFETY_MARGIN_MS = 5 * 60 * 1000;
const MIN_TOKEN_TTL_MS = 60_000;
// The token endpoint may not exist yet (PR #1280) or may be mid-outage:
// don't hammer it on every flush — back off for 5 minutes after any failure.
const TOKEN_FETCH_BACKOFF_MS = 5 * 60 * 1000;
// The token endpoint may hang instead of failing fast — never let a stuck
// fetch keep the flush timer's identify()/resolveAuthHeader() path blocked.
const TOKEN_FETCH_TIMEOUT_MS = 5_000;

let cached: CachedToken | null = null;
let lastFailureAt = 0;

/** Drops the cached telemetry token and its failure backoff. Safe to call any time. */
export function clearTelemetryTokenCache(): void {
  cached = null;
  lastFailureAt = 0;
}

export function __resetTelemetryTokenCacheForTests(): void {
  clearTelemetryTokenCache();
}

export async function getTelemetryToken(
  config: TelemetryTokenConfig,
): Promise<string | null> {
  if (!config.apiUrl) return null;

  // Read the app's CURRENT auth token first: it is the cache key, so a
  // stale cache entry can be dropped before it is ever handed out.
  const appToken = config.getAppAuthToken();
  const identityKey = appToken ?? "";
  if (cached && cached.identityKey !== identityKey) {
    cached = null;
    lastFailureAt = 0;
  }

  const now = Date.now();
  if (cached && cached.expiresAt > now) return cached.token;
  if (lastFailureAt > 0 && now - lastFailureAt < TOKEN_FETCH_BACKOFF_MS) {
    return null;
  }

  if (!appToken) return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${config.apiUrl}/telemetry/token`, {
      headers: { Authorization: `Bearer ${appToken}` },
      signal: controller.signal,
    });
    if (!response.ok) {
      lastFailureAt = now;
      return null;
    }

    const body = (await response.json()) as {
      token?: string;
      expires_in?: number;
    };
    if (!body.token) {
      lastFailureAt = now;
      return null;
    }

    const ttlMs = (body.expires_in ?? 12 * 60 * 60) * 1000;
    cached = {
      token: body.token,
      expiresAt: now + Math.max(ttlMs - TOKEN_SAFETY_MARGIN_MS, MIN_TOKEN_TTL_MS),
      identityKey,
    };
    return cached.token;
  } catch {
    lastFailureAt = now;
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
